import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { EtainablSpinner } from '@/components/etainabl-spinner'
import { useKbArticles } from '@/queries/kb'
import { useAuth0 } from '@auth0/auth0-react'
import { track } from '@/lib/analytics'
import dayjs from 'dayjs'
import { KbRestrictedBadge } from './KbRestrictedBadge'

export function KbSearchView() {
  const [q, setQ] = useState('')
  const { data, isLoading } = useKbArticles(q ? { q } : {})

  useEffect(() => {
    const trimmed = q.trim()
    if (trimmed.length < 3) return
    const handle = setTimeout(() => {
      track('kb_search_performed', {
        query_length: trimmed.length,
        result_count: data?.data.length ?? 0
      })
    }, 800)
    return () => clearTimeout(handle)
  }, [q, data?.data.length])
  const { user } = useAuth0()
  const isAdmin = Boolean((user as Record<string, unknown> | undefined)?.['https://app.etainabl.com/isAdmin'])

  return (
    <div className="container mx-auto max-w-3xl p-6 space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold flex-1">Knowledgebase</h1>
        {isAdmin && (
          <Link to="/admin/kb" className="text-sm text-muted-foreground hover:text-foreground underline">
            Manage articles
          </Link>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search articles…"
          className="pl-9"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      {isLoading && (
        <div className="py-8 flex justify-center">
          <EtainablSpinner size="sm" />
        </div>
      )}

      {!isLoading && (data?.data.length ?? 0) === 0 && (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {q ? `No articles match "${q}".` : 'No articles published yet.'}
        </p>
      )}

      <ul className="divide-y rounded border bg-background">
        {data?.data.map(a => (
          <li key={a._id}>
            <Link
              to={`/kb/${a.slug}` as never}
              className="block px-4 py-3 hover:bg-muted transition-colors"
            >
              <div className="font-medium">{a.title}</div>
              {a.subtitle && <div className="text-sm text-muted-foreground mt-0.5">{a.subtitle}</div>}
              <div className="text-xs text-muted-foreground mt-1">
                Updated {dayjs(a.updatedAt).fromNow()}
                {!a.isPublished && <span className="ml-2 text-amber-600">· Draft</span>}
                {isAdmin && <KbRestrictedBadge article={a} className="ml-2" />}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
